import type { SnapshotOf, StatusOf, TopicFamily, TopicOf, UpdateOf } from "@trader/shared/topics/index"
import {
  Heartbeat,
  Pong,
  SubscribeFailed,
  type SubscribeFailedReason,
  TopicKey,
  TopicResync,
  Welcome
} from "@trader/shared/WsProtocol"
import { Exit, Option, Schema } from "effect"

// Topic subscriptions over the app-wide WebSocket (docs/design/0005, "WebSocket").
// Plain TS with no DOM access, so it is tested without a browser; live.ts wires it
// to the socket.
//
// One server subscription per topic key, shared by every local listener. A
// listener gets a Snapshot, then Updates in seq order, plus latest-wins Status.
// Whenever the stream can't be trusted (a seq gap, a Resync, a reconnect) the key
// is subscribed again and every listener gets a fresh Snapshot, which replaces
// whatever it had.

// --- Incoming frames ---------------------------------------------------------------

// Topic frames are decoded in two steps: the envelope here, the data with the
// subscribing family's own schemas (so each listener gets its family's types).
const SnapshotFrame = Schema.TaggedStruct("Snapshot", { key: TopicKey, seq: Schema.Int, data: Schema.Unknown })
const UpdateFrame = Schema.TaggedStruct("Update", { key: TopicKey, seq: Schema.Int, data: Schema.Unknown })
const StatusFrame = Schema.TaggedStruct("Status", { key: TopicKey, data: Schema.Unknown })

export const TopicFrame = Schema.Union([SnapshotFrame, UpdateFrame, StatusFrame, TopicResync, SubscribeFailed])
export type TopicFrame = typeof TopicFrame.Type

/** Every server message, with topic data still undecoded. */
export const IncomingFrame = Schema.Union([Welcome, Pong, Heartbeat, SnapshotFrame, UpdateFrame, StatusFrame, TopicResync, SubscribeFailed])
export type IncomingFrame = typeof IncomingFrame.Type

const decodeIncoming = Schema.decodeUnknownOption(Schema.fromJsonString(IncomingFrame))

/** Decodes one WebSocket frame; undefined for anything that isn't a server message we know. */
export const decodeFrame = (data: unknown): IncomingFrame | undefined =>
  typeof data === "string" ? Option.getOrUndefined(decodeIncoming(data)) : undefined

// --- Subscriptions --------------------------------------------------------------------

export interface TopicHandlers<F extends TopicFamily> {
  /** The full state as of `seq`. First, and again after every resync: replace what you have. */
  readonly onSnapshot: (data: SnapshotOf<F>, seq: number) => void
  /** One change after the snapshot, in order (seq = previous + 1). */
  readonly onUpdate: (data: UpdateOf<F>, seq: number) => void
  /** Latest-wins status; each one replaces the previous. Only after a snapshot. */
  readonly onStatus?: (data: StatusOf<F>) => void
  /** The server refused the subscription. It is tried again on reconnect, or when someone subscribes to the key again. */
  readonly onError?: (error: SubscribeFailedReason) => void
}

export interface Timer {
  readonly cancel: () => void
}

export interface TopicRouterOptions {
  /** Sends one frame; only called while connected. */
  readonly send: (text: string) => void
  readonly schedule: (run: () => void, delayMs: number) => Timer
  readonly now: () => number
  /** A frame whose data doesn't match its family's schema (server and client out of step). */
  readonly onDecodeError?: (key: string, data: unknown) => void
  /** A listener threw. The others still get the frame, and the stream carries on. */
  readonly onListenerError?: (key: string, error: unknown) => void
}

/** A key whose stream stayed healthy this long starts its backoff over. */
export const HEALTHY_AFTER_MS = 10_000
export const MAX_RESUBSCRIBE_DELAY_MS = 10_000

/**
 * The wait before resubscribing after the n-th trouble in a row (1-based): the
 * first is immediate, then 250 ms doubling up to MAX_RESUBSCRIBE_DELAY_MS, so a
 * producer that keeps failing can't turn into a hot loop.
 */
export const resubscribeDelay = (troubles: number): number =>
  troubles <= 1 ? 0 : Math.min(250 * 2 ** (troubles - 2), MAX_RESUBSCRIBE_DELAY_MS)

/** One local listener, typed by its family through the closures. */
interface Listener {
  /** Has had a snapshot: only then does it get updates and status. */
  ready: boolean
  readonly snapshot: (data: unknown, seq: number) => boolean
  readonly update: (data: unknown, seq: number) => boolean
  readonly status: (data: unknown) => boolean
  readonly error: (error: SubscribeFailedReason) => void
}

type Phase =
  /** Subscribe sent (or due once connected); waiting for its snapshot. Updates are ignored. */
  | "awaiting"
  | "live"
  /** The server refused it; waiting for a reconnect or another subscribe. */
  | "failed"

interface Entry {
  readonly key: string
  /** The Subscribe frame, encoded once. */
  readonly subscribeText: string
  readonly listeners: Set<Listener>
  phase: Phase
  /** The last seq applied (when live). */
  seq: number
  liveSince: number
  troubles: number
  retry: Timer | undefined
}

/**
 * Decodes with the family's schemas and calls the handlers. Each returns false
 * when the data doesn't decode; a handler that throws is reported, not rethrown.
 */
const makeListener = <F extends TopicFamily>(
  family: F,
  handlers: TopicHandlers<F>,
  report: (error: unknown) => void
): Listener => {
  const decodeSnapshot = Schema.decodeUnknownExit(family.snapshot)
  const decodeUpdate = Schema.decodeUnknownExit(family.update)
  const decodeStatus = Schema.decodeUnknownExit(family.status)
  const guard = (run: () => void): true => {
    try {
      run()
    } catch (error) {
      report(error)
    }
    return true
  }
  return {
    ready: false,
    snapshot: (data, seq) => {
      const decoded = decodeSnapshot(data)
      return Exit.isSuccess(decoded) && guard(() => handlers.onSnapshot(decoded.value, seq))
    },
    update: (data, seq) => {
      const decoded = decodeUpdate(data)
      return Exit.isSuccess(decoded) && guard(() => handlers.onUpdate(decoded.value, seq))
    },
    status: (data) => {
      const decoded = decodeStatus(data)
      return Exit.isSuccess(decoded) && guard(() => handlers.onStatus?.(decoded.value))
    },
    error: (error) => void guard(() => handlers.onError?.(error))
  }
}

/**
 * Routes topic frames to listeners and keeps each key's stream consistent:
 * ref-counted subscriptions, seq checking, resubscribing on gaps, resyncs and
 * reconnects (with backoff when troubles repeat).
 */
export class TopicRouter {
  private readonly entries = new Map<string, Entry>()
  private readonly options: TopicRouterOptions
  private connected = false

  constructor(options: TopicRouterOptions) {
    this.options = options
  }

  /** Listens to a topic until the returned function is called. */
  subscribe<F extends TopicFamily>(family: F, topic: TopicOf<F>, handlers: TopicHandlers<F>): () => void {
    const key = family.key(topic)
    const listener = makeListener(family, handlers, (error) => this.options.onListenerError?.(key, error))
    let entry = this.entries.get(key)
    if (entry === undefined) {
      const encodeTopic = Schema.encodeSync(family.topic)
      entry = {
        key,
        subscribeText: JSON.stringify({ _tag: "Subscribe", topic: encodeTopic(topic) }),
        listeners: new Set([listener]),
        phase: "awaiting",
        seq: 0,
        liveSince: 0,
        troubles: 0,
        retry: undefined
      }
      this.entries.set(key, entry)
      this.sendSubscribe(entry)
    } else {
      entry.listeners.add(listener)
      // A live or refused key is subscribed again, so the newcomer gets a snapshot
      // (the others get one too, which is harmless). An awaiting one already will.
      if (entry.phase !== "awaiting") this.resubscribe(entry, 0)
    }
    const owner = entry
    let active = true
    return () => {
      if (!active) return
      active = false
      owner.listeners.delete(listener)
      if (owner.listeners.size > 0 || this.entries.get(key) !== owner) return
      owner.retry?.cancel()
      this.entries.delete(key)
      if (this.connected) this.options.send(JSON.stringify({ _tag: "Unsubscribe", key }))
    }
  }

  /** The socket opened: (re)subscribe every key. */
  connectionOpened(): void {
    this.connected = true
    for (const entry of this.entries.values()) {
      entry.retry?.cancel()
      entry.retry = undefined
      entry.phase = "awaiting"
      this.sendSubscribe(entry)
    }
  }

  /** The socket closed: every key waits for a fresh snapshot after the reconnect. */
  connectionClosed(): void {
    this.connected = false
    for (const entry of this.entries.values()) {
      entry.retry?.cancel()
      entry.retry = undefined
      entry.phase = "awaiting"
    }
  }

  /** Handles one topic frame from the server. */
  receive(frame: TopicFrame): void {
    const entry = this.entries.get(frame.key)
    if (entry === undefined) return // unsubscribed, or a frame still in flight from before
    switch (frame._tag) {
      case "Snapshot": {
        entry.phase = "live"
        entry.seq = frame.seq
        entry.liveSince = this.options.now()
        entry.retry?.cancel()
        entry.retry = undefined
        for (const listener of entry.listeners) {
          if (!listener.snapshot(frame.data, frame.seq)) return this.decodeFailed(entry, frame.data)
          listener.ready = true
        }
        return
      }
      case "Update": {
        if (entry.phase !== "live" || frame.seq <= entry.seq) return // not ours yet, or already applied
        if (frame.seq !== entry.seq + 1) return this.trouble(entry)
        entry.seq = frame.seq
        for (const listener of entry.listeners) {
          if (listener.ready && !listener.update(frame.data, frame.seq)) return this.decodeFailed(entry, frame.data)
        }
        return
      }
      case "Status": {
        if (entry.phase !== "live") return
        for (const listener of entry.listeners) {
          if (listener.ready && !listener.status(frame.data)) return this.decodeFailed(entry, frame.data)
        }
        return
      }
      case "Resync":
        return this.trouble(entry)
      case "SubscribeFailed": {
        if (entry.phase === "live") return // from an earlier attempt; the stream we have is fine
        entry.phase = "failed"
        entry.retry?.cancel()
        entry.retry = undefined
        for (const listener of entry.listeners) listener.error(frame.error)
        return
      }
    }
  }

  /** Keys with at least one listener (for diagnostics and tests). */
  keys(): ReadonlyArray<string> {
    return [...this.entries.keys()]
  }

  private sendSubscribe(entry: Entry): void {
    if (this.connected) this.options.send(entry.subscribeText)
  }

  /** The stream can't be trusted: drop it and subscribe again, backing off if this keeps happening. */
  private trouble(entry: Entry): void {
    const now = this.options.now()
    if (entry.phase === "live" && now - entry.liveSince >= HEALTHY_AFTER_MS) entry.troubles = 0
    entry.troubles++
    this.resubscribe(entry, resubscribeDelay(entry.troubles))
  }

  private resubscribe(entry: Entry, delayMs: number): void {
    entry.phase = "awaiting"
    entry.retry?.cancel()
    entry.retry = undefined
    if (delayMs <= 0) return this.sendSubscribe(entry)
    entry.retry = this.options.schedule(() => {
      entry.retry = undefined
      if (this.entries.get(entry.key) === entry && entry.phase === "awaiting") this.sendSubscribe(entry)
    }, delayMs)
  }

  private decodeFailed(entry: Entry, data: unknown): void {
    this.options.onDecodeError?.(entry.key, data)
    this.trouble(entry)
  }
}
